import React from 'react'
import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { GetStaticProps } from 'next'

import { Faq } from '@ecm/data/Faq'
import { BenefitsBlock } from '@ecm/data/LandingPage'
import { Testimonial } from '@ecm/data/Testimonial'
import { runEff } from '@ecm/effect'
import FaqBlock from '@ecm/ui/component/block/Faq'
import { Explanation } from '@ecm/ui/component/block/HowItWorks'
import { Benefit, BenefitGrid } from '@ecm/ui/component/grid/Benefits'
import { FlowConnector, FlowFrame, FlowGrid } from '@ecm/ui/component/grid/Flow'
import Color from '@ecm/ui/element/Color'
import Feature from '@ecm/ui/element/Feature'
import MainContainer from '@ecm/ui/element/MainContainer'
import TestimonialCarousel from '@ecm/ui/element/Testimonial'
import DefaultLayout from '@ecm/ui/layout/Default'

import locationPinIcon from '/public/images/illustration/location-pin.svg'

const frame1: Explanation = {
  description: 'Choose an address from our national network of 80+ addresses.',
  header: 'Select your new business address',
  icon: 'location-pin',
  position: 1,
}
const frame2: Explanation = {
  description:
    'We provide unique suite numbers for each Earth Class Mail business address you select. As we receive your postal mail, we will deliver it to your account.',
  header: 'We assign a suite number',
  icon: 'mail',
  position: 2,
}
const frame3: Explanation = {
  description:
    "Share your new address with your customers and teams. It's a perfect addition to your brand. You can even use it in your email footer to stay CANSPAM compliant.",
  header: 'Use your new address everywhere',
  icon: 'earth',
  position: 3,
}

const benefitGridHeaderDefault = {
  heading: 'All the benefits of a professional address, none of the hassle',
  subHeading: 'Business Address Benefits',
}

const benefit1 = {
  description:
    "Whether you're an entrepreneur, a digital nomad, or a remote company, you can do business on your terms. We'll take care of the postal mail.",
  iconName: 'logomark',
  title: 'Work from anywhere',
}
const benefit2 = {
  description:
    'Your business address is an important piece of your brand. Using the right address can help create a great impression.',
  iconName: 'scan',
  title: 'Complete your business identity',
}
const benefit3 = {
  description:
    "Reach beyond your neighborhood. Expand your business' footprint to any U.S. state with a local street address or PO box.",
  iconName: 'action',
  title: 'Capture new customers',
}

const defaultBenefits = [benefit1, benefit2, benefit3]
const defaultHowItWorksData = [frame1, frame2, frame3]

type VirtualBusinessAddressPageProps = {
  heroHeading?: string
  faqs: Faq[]
  testimonial: Testimonial
  primaryCtaText?: string
  benefitsBlock?: BenefitsBlock | null
  howItWorksData?: Explanation[] | null
  illustrationSrc?: string | null
  title?: string
  labeledIcon?: string | null
  labeledIconText?: string | null
  heroDescription?: string | null
}

export const VirtualBusinessAddressPage = ({
  heroHeading,
  benefitsBlock,
  howItWorksData,
  faqs,
  testimonial,
  illustrationSrc,
  primaryCtaText,
  title,
  labeledIcon,
  labeledIconText,
  heroDescription,
}: VirtualBusinessAddressPageProps) => {
  const benefits = benefitsBlock?.benefits || defaultBenefits
  const howItWorks = howItWorksData || defaultHowItWorksData
  const benefitsHeader = benefitsBlock?.header || benefitGridHeaderDefault.heading
  const benefitsTitle = benefitsBlock?.title || benefitGridHeaderDefault.subHeading

  return (
    <DefaultLayout seoTitle={title} hideFinalCta seoPage={'virtual-business-address'}>
      <Grid container justifyContent="space-around" bgcolor={Color.smoke.normal}>
        <Feature
          asHero
          description={
            heroDescription ||
            'Our national network of 80+ U.S. addresses and PO boxes is the perfect solution for your business. Share your virtual address with customers, business partners, and investors.'
          }
          icon={labeledIcon ?? 'location-pin'}
          illustrationSrc={illustrationSrc ?? locationPinIcon}
          imagePosition="right"
          primaryCta={primaryCtaText || 'Sign up today'}
          primaryHref="/pricing"
          tag={labeledIconText || 'Business Address'}
          title={heroHeading || 'Get a virtual address for your business'}
        />
      </Grid>
      <MainContainer sx={{ paddingY: { md: '4em', xs: '2em' } }}>
        <Typography variant="h3" component="h2" textAlign="center">
          How Business Address Works
        </Typography>
        <FlowGrid frameCount={3}>
          {howItWorks.map((element, index) => (
            <React.Fragment key={element.header}>
              <FlowFrame heading={element.header} icon={element.icon} index={index + 1}>
                {element.description}
              </FlowFrame>
              {index != defaultHowItWorksData.length - 1 && (
                <FlowConnector icon={`connector-${element.position}`} index={element.position} />
              )}
            </React.Fragment>
          ))}
        </FlowGrid>
      </MainContainer>

      <Grid
        container
        direction="column"
        paddingY={{ md: '4em', xs: '2em' }}
        bgcolor={Color.smoke.normal}
      >
        <BenefitGrid heading={benefitsHeader} subHeading={benefitsTitle}>
          {benefits.map(benefit => (
            <Benefit
              key={benefit.title}
              width="22em"
              icon={benefit.iconName}
              heading={benefit.title}
            >
              {benefit.description}
            </Benefit>
          ))}
        </BenefitGrid>
      </Grid>

      {testimonial && (
        <Grid
          justifyContent="space-around"
          paddingX={{ lg: '64px', md: '40px', sm: '24px', xs: '16px' }}
        >
          <TestimonialCarousel testimonials={[testimonial]} />
        </Grid>
      )}

      <FaqBlock showFinePrint faqs={faqs} />
    </DefaultLayout>
  )
}

export default VirtualBusinessAddressPage

export const getStaticProps: GetStaticProps = async () => {
  const faqs = await runEff(eff => eff.getFaqsByPage(['/virtual-business-address']))

  const testimonial = await runEff(async eff => {
    try {
      return await eff.getTestimonial('james-gadsby-2')
    } catch (e) {
      return null
    }
  })

  return {
    props: {
      faqs,
      testimonial,
    },
  }
}
